// SCR-STAFF-1206: کتابخانه محاسبه‌گر مستأجر (US-CFG-053).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { docKeys } from '../documents/common';
import { cfgKeys, SCR_STAFF_1206 } from './common';

interface CalculatorSummary {
  code: string;
  version: number;
  title: string;
  specialties: string[];
  license: string;
  isSample: boolean;
}

export function CalculatorLibraryPage() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canAdd = access.accessMode === 'Full' && access.can('doc.form_template.manage');
  const [adding, setAdding] = useState<CalculatorSummary | null>(null);
  const [templateId, setTemplateId] = useState('');

  const list = useQuery({
    queryKey: cfgKeys.calculators(session?.tenantId),
    enabled: canAdd,
    queryFn: async () =>
      (await unwrap(tenantApi.GET('/api/v1/configuration/calculators', { params: { query: {} } }))) as CalculatorSummary[],
  });

  const templates = useQuery({
    queryKey: docKeys.formTemplates(session?.tenantId),
    enabled: adding !== null && canAdd,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-templates', { params: { query: {} } })),
  });

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/configuration/calculators/{code}:add', {
          params: { path: { code: adding!.code } },
          headers: { 'Idempotency-Key': key.current() },
          body: { version: adding!.version, formTemplateId: templateId || null },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setAdding(null);
      setTemplateId('');
      void queryClient.invalidateQueries({ queryKey: cfgKeys.calculators(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<CalculatorSummary>[] = [
    {
      id: 'title',
      headerKey: 'cfg:calculators.title',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.title}</span>
          {row.isSample ? <Chip size="small" label={t('calculators.sample')} data-testid="cfg-calculator-sample" /> : null}
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'code', headerKey: 'cfg:calculators.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'version', headerKey: 'cfg:calculators.version', cell: (row) => row.version },
    { id: 'license', headerKey: 'cfg:calculators.license', cell: (row) => row.license },
    {
      id: 'specialties',
      headerKey: 'cfg:calculators.specialties',
      cell: (row) => row.specialties.join(t('common:list_separator')),
    },
    {
      id: 'actions',
      headerKey: 'common:actions',
      cell: (row) =>
        canAdd ? (
          <Button size="small" onClick={() => setAdding(row)} data-testid="cfg-calculator-add">
            {t('calculators.add')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage screenId={SCR_STAFF_1206} titleKey="cfg:calculators.title">
      <Stack spacing={2} data-testid="calculator-library-page">
        <Alert severity="info">{t('calculators.decisionSupport')}</Alert>
        <DataTable
          captionKey="cfg:calculators.title"
          columns={columns}
          rows={list.data}
          getRowId={(row) => `${row.code}@${row.version}`}
          isLoading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          emptyTitleKey="cfg:calculators.empty"
        />
      </Stack>
      <FormDialog
        open={adding !== null}
        titleKey="cfg:calculators.add"
        onSubmit={() => add.mutate()}
        onClose={() => setAdding(null)}
        isDirty
        isSubmitting={add.isPending}
        submitError={add.error}
      >
        <TextField
          select
          label={t('calculators.selectTemplate')}
          value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'cfg-calculator-template' } }}
        >
          <MenuItem value="">—</MenuItem>
          {(templates.data?.items ?? []).map((row) => (
            <MenuItem key={row.id} value={row.id}>
              {row.name}
            </MenuItem>
          ))}
        </TextField>
      </FormDialog>
    </ListPage>
  );
}
